import '@fontsource/inter/100.css';
import '@fontsource/inter/300.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/700.css';
import './styles/index.css';

import type { UserLocalStorage } from './types/localStorage';

import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import TagManager from 'react-gtm-module';
import z from 'zod';

import { getEnv } from './lib/env';
import { queryClient } from './lib/flotiq-client/queryClient';
import FlotiqPlugins from './lib/flotiq-plugins/flotiqPluginsRegistry';
import { getMaskedOrganizationName } from './lib/helpers';

import ErrorFallback from './components/ErrorFallback/ErrorFallback';

import i18n from './i18n';
import App from './pages/App/App';
import reportWebVitals from './reportWebVitals';

if (process.env.NODE_ENV === 'production') {
  Sentry.init({
    beforeSend: (event: Sentry.ErrorEvent) => {
      const isLocalPlugin = !!event.exception?.values?.find(
        ({ stacktrace }) =>
          !!stacktrace?.frames?.find(
            ({ filename }) => !!filename?.includes('plugins___local_'),
          ),
      );
      if (isLocalPlugin) return null;
      return event;
    },
    dsn: getEnv('VITE_SENTRY_DSN'),
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 1.0,
    environment: getEnv('VITE_SENTRY_ENVIRONMENT'),
    ignoreErrors: ['Plugin does not have permission to'],
  });
}

if (getEnv('VITE_GTM_ID')) {
  const user = JSON.parse(
    localStorage.getItem('cms.user') || '{}',
  ) as UserLocalStorage;

  TagManager.initialize({
    gtmId: getEnv('VITE_GTM_ID') || '',
    ...(user?.data
      ? {
          dataLayer: {
            user_roles: user.data.roles,
            user_id: user.data.id,
            organization_id: user.data.organization,
            organization_name: getMaskedOrganizationName(
              user.data.organization_name,
            ),
            plan_id: user.data.limits_plan?.id,
            plan_name: user.data.limits_plan?.name,
            space_id: user.data.default_space?.id,
          },
        }
      : {}),
  });
}

const rootElement = document.getElementById('root');
const root = createRoot(rootElement as HTMLElement);

FlotiqPlugins.init();

const getErrorFallbackPage: Sentry.FallbackRender = (event) => (
  <ErrorFallback {...event} />
);

/**
 * Customize zod errors for required fields
 */
z.config({
  customError: (iss) => {
    if (iss.code === 'invalid_type')
      return i18n.t('ContentForm.Errors.Required');
  },
});

root.render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={getErrorFallbackPage}>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
